import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { getEcho } from '../echo';
import presenceService from '../services/presenceService';

const PresenceContext = createContext({ statuses: {}, clock: 0, watch: () => () => {} });

function sessionId() {
  if (window.crypto?.randomUUID) return window.crypto.randomUUID();
  // This is a per-mount identifier, never an authentication credential.
  const bytes = window.crypto?.getRandomValues
    ? window.crypto.getRandomValues(new Uint8Array(16))
    : Uint8Array.from({ length: 16 }, () => Math.floor(Math.random() * 256));
  bytes[6] = (bytes[6] & 15) | 64;
  bytes[8] = (bytes[8] & 63) | 128;
  const hex = [...bytes].map(byte => byte.toString(16).padStart(2, '0')).join('');
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

export function TalkPresenceProvider({ token, children }) {
  const [statuses, setStatuses] = useState({});
  const [clock, setClock] = useState(() => performance.now());
  const [watched, setWatched] = useState({});
  const watchIds = Object.keys(watched).sort((a, b) => Number(a) - Number(b)).join(',');
  const idsRef = useRef([]);
  idsRef.current = watchIds ? watchIds.split(',').map(Number) : [];
  const snapshotRef = useRef(() => {});

  const watch = useCallback(id => {
    if (!id) return () => {};
    setWatched(previous => ({ ...previous, [id]: (previous[id] || 0) + 1 }));
    return () => setWatched(previous => {
      const next = { ...previous };
      if (next[id] > 1) next[id] -= 1;
      else delete next[id];
      return next;
    });
  }, []);

  useEffect(() => {
    let user;
    try { user = JSON.parse(localStorage.getItem('user')); } catch (_) { return undefined; }
    if (!token || !user?.id) return undefined;
    let stopped = false;
    let sequence = 0;
    let heartbeatPending = false;
    let snapshotController;
    let snapshotVersion = 0;
    const session = sessionId();
    const ownsToken = () => localStorage.getItem('token') === token;
    const active = () => document.visibilityState !== 'hidden' && navigator.onLine !== false && ownsToken();
    setStatuses({});

    const merge = entries => {
      if (stopped || !ownsToken()) return;
      const receivedAt = performance.now();
      setClock(receivedAt);
      setStatuses(previous => {
        const next = { ...previous };
        entries.forEach(entry => {
          if (!idsRef.current.includes(Number(entry.user_id))) return;
          const observed = Date.parse(entry.observed_at);
          const existing = next[entry.user_id];
          if (!Number.isFinite(observed) || (existing && (observed < existing.observed
            || (observed === existing.observed && entry.observed_at <= existing.observed_at)))) return;
          const remaining = Math.max(0, Date.parse(entry.expires_at) - observed) || 0;
          next[entry.user_id] = { ...entry, observed, validUntil: receivedAt + Math.min(remaining, 90000) };
        });
        return next;
      });
    };

    const snapshot = async () => {
      if (stopped || !active()) return;
      snapshotController?.abort();
      snapshotController = new AbortController();
      const controller = snapshotController;
      const version = ++snapshotVersion;
      const ids = [...idsRef.current];
      try {
        const pages = [];
        for (let offset = 0; offset < ids.length; offset += 100) {
          pages.push(presenceService.statuses(ids.slice(offset, offset + 100), { signal: controller.signal }));
        }
        const results = await Promise.all(pages);
        if (stopped || version !== snapshotVersion) return;
        const entries = results.filter(result => result?.status).flatMap(result => result.data || []);
        merge(entries);
        // A removed conversation member must not retain an old authorized status.
        const returned = new Set(entries.map(entry => Number(entry.user_id)));
        if (results.every(result => result?.status)) {
          setStatuses(previous => Object.fromEntries(Object.entries(previous)
            .filter(([id]) => !ids.includes(Number(id)) || returned.has(Number(id)))));
        }
      } catch (_) {
        // Keep the saved last-seen time; online leases expire locally during outages.
      }
    };
    snapshotRef.current = snapshot;

    const send = isActive => {
      if (!ownsToken() || (isActive && (stopped || !active()))) return Promise.resolve();
      return presenceService.heartbeat({ session_id: session, sequence: ++sequence, active: isActive });
    };
    const refresh = () => {
      if (stopped || !active()) return;
      if (!heartbeatPending) {
        heartbeatPending = true;
        Promise.resolve().then(() => send(true)).catch(() => {}).finally(() => { heartbeatPending = false; });
      }
      snapshot();
    };
    const pause = () => {
      snapshotController?.abort();
      heartbeatPending = false;
      send(false)?.catch(() => {});
    };
    const visibility = () => { if (active()) refresh(); else pause(); };
    const echo = getEcho();
    const channel = echo?.private(`App.Models.User.${user.id}`);
    const presenceEvent = entry => merge([entry]);
    channel?.listen('.talk.presence.updated', presenceEvent);
    const connection = echo?.connector?.pusher?.connection;
    connection?.bind('connected', refresh);
    document.addEventListener('visibilitychange', visibility);
    window.addEventListener('online', refresh);
    window.addEventListener('offline', pause);
    window.addEventListener('focus', refresh);
    window.addEventListener('pageshow', refresh);
    window.addEventListener('pagehide', pause);
    const heartbeatTimer = setInterval(refresh, 30000);
    const leaseTimer = setInterval(() => setClock(performance.now()), 5000);
    refresh();
    return () => {
      stopped = true;
      snapshotRef.current = () => {};
      snapshotController?.abort();
      clearInterval(heartbeatTimer);
      clearInterval(leaseTimer);
      document.removeEventListener('visibilitychange', visibility);
      window.removeEventListener('online', refresh);
      window.removeEventListener('offline', pause);
      window.removeEventListener('focus', refresh);
      window.removeEventListener('pageshow', refresh);
      window.removeEventListener('pagehide', pause);
      channel?.stopListening('.talk.presence.updated', presenceEvent);
      connection?.unbind('connected', refresh);
      send(false)?.catch(() => {});
    };
  }, [token]);

  useEffect(() => { snapshotRef.current(); }, [watchIds]);
  const value = useMemo(() => ({ statuses, clock, watch }), [statuses, clock, watch]);
  return <PresenceContext.Provider value={value}>{children}</PresenceContext.Provider>;
}

export function useUserPresence(userId, fallbackLastSeen = null) {
  const { statuses, clock, watch } = useContext(PresenceContext);
  useEffect(() => watch(userId), [watch, userId]);
  const status = statuses[userId];
  return {
    is_online: Boolean(status?.is_online && status.validUntil > clock),
    last_seen_at: status?.last_seen_at || fallbackLastSeen,
  };
}
